import { isTauri } from '@heidi/runtime/platform';
import { isChatExportDownloadUrl } from './isChatExportDownloadUrl';

/** Native or app-owned saver. Return false when the user cancels. */
export type SaveExportBlob = (blob: Blob, filename: string) => Promise<boolean>;

const FILENAME_STAR = /filename\*=UTF-8''([^;]+)/i;
const FILENAME_QUOTED = /filename="([^"]*)"/i;

function withSearchParam(url: string, key: string, value: string): string {
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set(key, value);
  return parsed.toString();
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const encoded = FILENAME_STAR.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return null;
    }
  }
  return FILENAME_QUOTED.exec(header)?.[1] ?? null;
}

function safeFilename(name: string | null | undefined): string | null {
  if (!name) return null;
  const cleaned = name
    .replace(/[\\/]/g, '')
    .replace(/[\r\n\0]/g, '')
    .trim();
  return cleaned || null;
}

/** A trailing short suffix, not a dot that happens to sit inside a title. */
const FILE_EXTENSION = /\.[A-Za-z0-9]{1,8}$/;

function extensionForBlob(blobType: string): string | null {
  if (blobType.includes('pdf')) return '.pdf';
  if (blobType.includes('wordprocessingml')) return '.docx';
  if (blobType.includes('spreadsheetml')) return '.xlsx';
  if (blobType.includes('csv')) return '.csv';
  return null;
}

function withBlobExtension(name: string, blobType: string): string {
  if (FILE_EXTENSION.test(name)) return name;
  const extension = extensionForBlob(blobType);
  return extension ? `${name}${extension}` : name;
}

function chooseFilename(
  preferred: string | null | undefined,
  header: string | null,
  blobType: string
): string {
  const fromPreferred = safeFilename(preferred);
  const fromHeader = safeFilename(filenameFromContentDisposition(header));
  if (fromPreferred && FILE_EXTENSION.test(fromPreferred)) return fromPreferred;
  if (fromHeader && FILE_EXTENSION.test(fromHeader)) return fromHeader;
  if (fromPreferred) return withBlobExtension(fromPreferred, blobType);
  if (fromHeader) return withBlobExtension(fromHeader, blobType);
  const extension = extensionForBlob(blobType);
  return extension ? `download${extension}` : 'download';
}

function openAttachmentUrl(url: string): void {
  const target = isChatExportDownloadUrl(url)
    ? withSearchParam(url, 'attachment', '1')
    : url;
  window.open(target, '_blank', 'noopener,noreferrer');
}

function saveBlobWithAnchor(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Save a chat export. Proxy fetch stays on the API host, because S3 sends
 * no CORS headers for the app. Desktop without a native saver opens the
 * attachment URL, which the shell hands to the system browser.
 */
export async function downloadChatExport(
  url: string,
  filename?: string | null,
  saveBlob?: SaveExportBlob
): Promise<void> {
  if (!isChatExportDownloadUrl(url)) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  if (isTauri() && !saveBlob) {
    openAttachmentUrl(url);
    return;
  }

  try {
    const response = await fetch(withSearchParam(url, 'proxy', 'true'));
    if (!response.ok) {
      openAttachmentUrl(url);
      return;
    }
    const blob = await response.blob();
    const name = chooseFilename(
      filename,
      response.headers.get('content-disposition'),
      blob.type
    );
    if (saveBlob) {
      await saveBlob(blob, name);
      return;
    }
    saveBlobWithAnchor(blob, name);
  } catch {
    openAttachmentUrl(url);
  }
}
